import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { TableContext } from 'src/app/providers/TableProvider';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { GetDirectoryWorkflowRes, GetFormWorkflowRes } from 'src/shared/api/projects/types';
import { routesEnum } from 'src/shared/const';
import { setError } from 'src/shared/lib/utils';
import { FormType, TableSettingsForm } from 'src/shared/store/projects/types';

type SelectFormOptions = {
  syncUrl?: boolean;
};

const buildTableFormPath = (form: FormType) => {
  const alias = form.form_name?.trim();
  if (!alias) {
    return routesEnum.TABLE;
  }

  return `${routesEnum.TABLE}/${encodeURIComponent(alias)}`;
};

export const useGetForm = () => {
  const navigate = useNavigate();
  const {
    selectedForm,
    selectedProject,
    setSelectedForm,
    setIsTableSettingsLoading,
    setTableSettings,
  } = useContext(ProjectsContext);
  const { setTableData, resetTable } = useContext(TableContext);

  const handleSelectForm = useCallback(
    async (form: FormType, options?: SelectFormOptions) => {
      if (selectedForm?.id === form.id) {
        resetTable();
        return;
      }

      const shouldSyncUrl = options?.syncUrl !== false;
      if (shouldSyncUrl) {
        const query = new URLSearchParams();
        if (selectedProject?.id) {
          query.set('projectId', String(selectedProject.id));
        }
        query.set('formId', String(form.id));
        const queryString = query.toString();

        navigate(`${buildTableFormPath(form)}${queryString ? `?${queryString}` : ''}`);
      }

      setSelectedForm(form);
      setIsTableSettingsLoading(true);
      setTableSettings(null);
      setTableData([]);

      try {
        const [{ data }, workflowResponse] = await Promise.all([
          ProjectsService.getTableSettings({ formId: form.id }),
          form.is_directory
            ? ProjectsService.getDirectoryWorkflow(form.id).catch(() => null)
            : ProjectsService.getFormWorkflow(form.id).catch(() => null),
        ]);

        if (!data) {
          return;
        }
        const {
          Id,
          Alias,
          FormName,
          FormDescription,
          FormStructure,
          AppearanceSettings,
          ViewSettings,
          BusinessRules,
          FlkRules,
          IsDrectory,
        } = data;
        const workflowData = workflowResponse?.data;
        const isDirectory = Boolean(form.is_directory || IsDrectory);
        const formWorkflowData = !isDirectory
          ? (workflowData as GetFormWorkflowRes | undefined)
          : undefined;
        const directoryWorkflowData = isDirectory
          ? (workflowData as GetDirectoryWorkflowRes | undefined)
          : undefined;

        const tableSettings: TableSettingsForm = {
          id: Id || data?.['ID'],
          formDescription: FormDescription,
          alias: Alias,
          formName: Alias,
          connection: FormName,
          formStructure: FormStructure,
          isDirectory,
          appearanceSettings: AppearanceSettings,
          viewSettings: ViewSettings,
          businessRules: BusinessRules,
          flkRules: FlkRules,
          workflowSettings: formWorkflowData?.workflowSettings,
          directoryWorkflowSettings: directoryWorkflowData?.workflowSettings,
          workflowCurrentStatus: formWorkflowData?.currentStatus,
          workflowReadOnly: isDirectory ? false : formWorkflowData?.readOnly,
        };

        setTableSettings(tableSettings);
      } catch (e) {
        setError(e.response?.data?.error || 'Ошибка загрузки формы');
      } finally {
        setIsTableSettingsLoading(false);
      }
    },
    [
      navigate,
      resetTable,
      selectedForm?.id,
      selectedProject?.id,
      setIsTableSettingsLoading,
      setSelectedForm,
      setTableData,
      setTableSettings,
    ],
  );

  return { handleSelectForm };
};
